import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';

import AuthPageShell from '@components/layout/AuthPageShell';
import AuthFormCard, { authLinkSx, authPrimaryCtaSx } from '@components/layout/AuthFormCard';
import PhoneWithDialCode from '@components/auth/PhoneWithDialCode';
import { useAuthStore } from '@store/authStore';
import authService from '@services/authService';
import { GenderType } from '@types/index';
import { COUNTRIES, dialOptionForCountry, formatInternationalPhone } from '@utils/countries';
import { brandColors } from '@config/brandTokens';
import {
  evaluateRegistrationEligibility,
  getDigitalConsentAge,
  PARENT_MIN_AGE_TO_ADD_CHILD,
  rememberAddChildNext,
  clearAddChildNext,
} from '@utils/parentalConsent';
import { buildCheckEmailPath, buildSignInPath } from '@utils/authRouting';
import { isSafeAppReturnUrl } from '@utils/safeReturnUrl';

const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
  first_name: z.string().min(2, 'Name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  date_of_birth: z.string().min(1, 'Your date of birth is required'),
  gender: z.nativeEnum(GenderType, { required_error: 'Gender is required' }),
  phone_iso: z.string().optional(),
  phone_local: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  consent_terms: z.literal(true, { errorMap: () => ({ message: 'You must accept the Terms of Service and Privacy Policy' }) }),
  consent_data_processing: z.literal(true, { errorMap: () => ({ message: 'Data processing consent is required' }) }),
  consent_marketing: z.boolean().optional(),
});

type RegisterFormData = z.infer<typeof registerSchema>;
type RegisterIntent = 'self' | 'child';

const RegisterPlayerPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const incomingReturnUrl = searchParams.get('returnUrl');
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [intent, setIntent] = useState<RegisterIntent>('self');
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentData, setConsentData] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  const countryOptions = useMemo(
    () => COUNTRIES.map((c) => ({ value: c, label: `${c} (${getDigitalConsentAge(c)}+)` })),
    [],
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      country: 'India',
      phone_iso: 'IN',
      phone_local: '',
      consent_terms: false as unknown as true,
      consent_data_processing: false as unknown as true,
      consent_marketing: false,
    },
  });

  const watchedDob = watch('date_of_birth');
  const watchedCountry = watch('country');
  const watchedPhoneLocal = watch('phone_local');
  const eligibility = watchedDob
    ? evaluateRegistrationEligibility(watchedDob, watchedCountry)
    : null;
  const tooYoungToAddChild =
    intent === 'child' &&
    eligibility?.age != null &&
    !eligibility.requiresParentRegistration &&
    eligibility.age < PARENT_MIN_AGE_TO_ADD_CHILD;

  useEffect(() => {
    if (watchedPhoneLocal) return;
    setValue('phone_iso', dialOptionForCountry(watchedCountry).iso);
  }, [watchedCountry, watchedPhoneLocal, setValue]);

  const goToLogin = (options?: { returnUrl?: string; registered?: string; email?: string }) => {
    navigate(
      buildSignInPath({
        returnUrl: options?.returnUrl || incomingReturnUrl,
        registered: options?.registered,
        email: options?.email,
      }),
    );
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError('');
    const afterLogin = intent === 'child'
      ? '/player/profile?addChild=1'
      : isSafeAppReturnUrl(incomingReturnUrl)
        ? incomingReturnUrl
        : undefined;

    try {
      if (!data.date_of_birth) {
        setError('Your date of birth is required to confirm you are at or above the digital consent age.');
        setIsLoading(false);
        return;
      }
      const gate = evaluateRegistrationEligibility(data.date_of_birth, data.country);
      if (gate.requiresParentRegistration) {
        setError(
          'You are below the digital age of consent, so you cannot create an account. A parent or guardian should register first, then sign in and add you as a child from Profile.',
        );
        setIsLoading(false);
        return;
      }

      const response = await authService.registerPlayer({
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        date_of_birth: data.date_of_birth,
        gender: data.gender,
        country: data.country,
        phone: formatInternationalPhone(data.phone_iso, data.phone_local),
        is_below_18: false,
        registered_by_parent: false,
        consent_data_processing: consentData,
        consent_marketing: consentMarketing,
        consent_terms: consentTerms,
      });

      if (intent === 'child') rememberAddChildNext();
      else clearAddChildNext();

      const registeredCode = response.parentalConsentPending
        ? 'consent'
        : response.emailVerificationRequired
          ? 'verify'
          : intent === 'child'
            ? 'child'
            : 'player';

      if (response.emailVerificationRequired) {
        if (response.verificationLink) {
          sessionStorage.setItem('encycgames.verifyLink', response.verificationLink);
        }
        navigate(
          buildCheckEmailPath({
            email: data.email,
            role: 'player',
            returnUrl: afterLogin,
          }),
        );
        return;
      }

      if (response.accessToken && response.user) {
        login(response.user);
        toast.success(
          response.user.playerIdCode
            ? `You are registered as a player. Your Player ID is ${response.user.playerIdCode}.`
            : 'You are registered as a player.',
        );
        navigate(afterLogin || '/player/profile');
        return;
      }

      goToLogin({
        returnUrl: afterLogin,
        registered: registeredCode,
        email: data.email,
      });
    } catch (err: any) {
      const data = err.response?.data || {};
      const msg = data.message || 'Registration failed. Please try again.';
      if (
        data.code === 'ACCOUNT_EXISTS_UNVERIFIED'
        || data.code === 'EMAIL_NOT_VERIFIED'
        || data.data?.emailVerificationRequired
      ) {
        if (data.data?.verificationLink) {
          sessionStorage.setItem('encycgames.verifyLink', data.data.verificationLink);
        }
        navigate(
          buildCheckEmailPath({
            email: data.email || getValues('email'),
            role: 'player',
            returnUrl: afterLogin,
          }),
        );
        return;
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const addChildLoginPath = buildSignInPath({ returnUrl: '/player/profile?addChild=1' });
  const alreadyHaveAccount = (
    <Box sx={{ mt: 2.5, textAlign: 'center', ...authLinkSx }}>
      <Typography variant="body2" sx={{ color: brandColors.slate }}>
        Already have an account?{' '}
        <Link
          component={RouterLink}
          to={intent === 'child' ? addChildLoginPath : buildSignInPath({ returnUrl: incomingReturnUrl })}
          onClick={() => {
            if (intent === 'child') rememberAddChildNext();
            else clearAddChildNext();
          }}
        >
          {intent === 'child' ? 'Sign in and add child' : 'Sign in'}
        </Link>
      </Typography>
    </Box>
  );

  return (
    <AuthPageShell maxWidth="md">
      <Box sx={authLinkSx}>
        <AuthFormCard
          roleLabel="Player"
          title="Player registration"
          subtitle="Everyone is a Player. Register yourself first — then add children from your profile."
          chrome
          backTo={buildSignInPath({ returnUrl: incomingReturnUrl })}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                mb: 2.5,
                p: 2,
                border: '1px solid #d6dee8',
                borderRadius: 2,
                bgcolor: '#f8fafc',
              }}
            >
              <Typography sx={{ fontWeight: 800, color: brandColors.navy, mb: 0.5 }}>
                Who is this account for?
              </Typography>
              <Typography variant="body2" sx={{ color: brandColors.slate, mb: 1 }}>
                Same form either way. You always register as a player first.
              </Typography>
              <RadioGroup
                value={intent}
                onChange={(e) => setIntent(e.target.value as RegisterIntent)}
              >
                <FormControlLabel
                  value="self"
                  control={<Radio />}
                  label="For myself"
                />
                <FormControlLabel
                  value="child"
                  control={<Radio />}
                  label="For my child — I will add them after I sign in"
                />
              </RadioGroup>
            </Box>

            {intent === 'child' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                After you sign in, <strong>Add your child now</strong> is the first task on Profile. Child date of
                birth and digital consent are collected there. Parents / guardians must be{' '}
                {PARENT_MIN_AGE_TO_ADD_CHILD}+ to add a child.
              </Alert>
            )}

            {eligibility?.requiresParentRegistration && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {eligibility.reason} You cannot create this account. A parent or guardian at or above the digital
                consent age must register, then sign in and add the child.
              </Alert>
            )}

            {tooYoungToAddChild && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You can create your player account, but you must be {PARENT_MIN_AGE_TO_ADD_CHILD}+ to add a child.
                You are {eligibility?.age}.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('first_name')}
                  label="Your first name *"
                  fullWidth
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('last_name')}
                  label="Your last name *"
                  fullWidth
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register('date_of_birth')}
                  label="Your date of birth *"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.date_of_birth}
                  helperText={errors.date_of_birth?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      select
                      label="Your gender *"
                      fullWidth
                      error={!!errors.gender}
                      helperText={errors.gender?.message}
                    >
                      <MenuItem value={GenderType.MALE}>Male</MenuItem>
                      <MenuItem value={GenderType.FEMALE}>Female</MenuItem>
                      <MenuItem value={GenderType.OTHER}>Other</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('email')}
                  label="Your email *"
                  type="email"
                  fullWidth
                  autoComplete="email"
                  error={!!errors.email}
                  helperText={errors.email?.message || 'This is your login. You get a Player ID too.'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register('password')}
                  label="Password *"
                  type="password"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Country *"
                      fullWidth
                      error={!!errors.country}
                      helperText={`Consent age ${getDigitalConsentAge(watchedCountry)}+ in ${watchedCountry || 'this country'}`}
                    >
                      {countryOptions.map((c) => (
                        <MenuItem key={c.value} value={c.value}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone_iso"
                  control={control}
                  render={({ field: isoField }) => (
                    <Controller
                      name="phone_local"
                      control={control}
                      render={({ field: localField }) => (
                        <PhoneWithDialCode
                          iso={isoField.value || 'IN'}
                          local={localField.value || ''}
                          onIsoChange={isoField.onChange}
                          onLocalChange={localField.onChange}
                          error={!!errors.phone_local}
                          helperText={errors.phone_local?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Consent &amp; Legal (GDPR)
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentData}
                  onChange={(e) => {
                    setConsentData(e.target.checked);
                    setValue('consent_data_processing', e.target.checked as true, { shouldValidate: true });
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  <strong>Required:</strong> I consent to my personal data (name, performance data, contest
                  videos) being processed as described in the{' '}
                  <Link component={RouterLink} to="/privacy-policy" target="_blank">
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
            {errors.consent_data_processing && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {errors.consent_data_processing.message}
              </Alert>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentTerms}
                  onChange={(e) => {
                    setConsentTerms(e.target.checked);
                    setValue('consent_terms', e.target.checked as true, { shouldValidate: true });
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  <strong>Required:</strong> I accept the{' '}
                  <Link component={RouterLink} to="/privacy-policy" target="_blank">
                    Terms of Service and Privacy Policy
                  </Link>
                  .
                </Typography>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
            {errors.consent_terms && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {errors.consent_terms.message}
              </Alert>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentMarketing}
                  onChange={(e) => {
                    setConsentMarketing(e.target.checked);
                    setValue('consent_marketing', e.target.checked);
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  <em>Optional:</em> I agree to receive occasional performance tips, contest announcements, and
                  platform news.
                </Typography>
              }
              sx={{ alignItems: 'flex-start' }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 1, ...authPrimaryCtaSx }}
              disabled={isLoading || !consentData || !consentTerms || !!eligibility?.requiresParentRegistration}
            >
              {isLoading ? 'Registering...' : 'Register as a player'}
            </Button>
            {alreadyHaveAccount}
          </Box>
        </AuthFormCard>
      </Box>
    </AuthPageShell>
  );
};

export default RegisterPlayerPage;
